/**
 * TA Cockpit → Planning.
 *
 * Two things live under Planning and they answer different questions:
 * Hiring demand is "what do the departments need, and when" — collected from
 * unit leads. TA capacity planning is "can we deliver it with the team we have"
 * — the scenario model, and where the trimester's target is fixed. Demand is the
 * default because it is the input: the capacity scenario is only worth reading
 * once there is a number to test it against.
 */
import { useState } from 'react';
import { CockpitTabs } from './cockpitTabs';
import type { TACockpitData } from '../../types/taCockpit';
import { CapacityTab } from './capacity/CapacityTab';
import { HiringDemandTab } from './demand/HiringDemandTab';
import { currentTrimester } from './trimester';

type PlanningSubTab = 'demand' | 'capacity';

const PLANNING_TABS = [
  { value: 'demand', label: 'Hiring demand' },
  { value: 'capacity', label: 'TA capacity planning' },
] as const satisfies readonly { value: PlanningSubTab; label: string }[];

/**
 * Which collection round the board is for — derived from TODAY.
 *
 * It used to come from `ref_trimester_m1` on the cockpit sheet. That cell went
 * stale (it still read "May 2026" in September), so the board ran as a T2 round
 * and "hired this trimester" counted from May: 77 instead of 9. A trimester is
 * four calendar months; nothing about that needs maintaining.
 */
export function periodFromCockpit(_data?: TACockpitData | null): string {
  return currentTrimester().key;
}

export function PlanningTab({ data }: { data: TACockpitData | null }) {
  const [sub, setSub] = useState<PlanningSubTab>('demand');
  const period = periodFromCockpit(data);

  return (
    <div className="space-y-4">
      <CockpitTabs
        size="sub"
        value={sub}
        tabs={PLANNING_TABS}
        onChange={setSub}
        ariaLabel="Planning sections"
      />

      {sub === 'demand' ? (
        /*
         * The sheet is gone from the capacity side entirely: the team is a saved roster and the
         * assumptions are edited on screen, so there is nothing left for `ref_hpm_*` and
         * `base_fte_*` to seed. Demand, the target and every figure anyone acts on come from the
         * Hiring Database.
         */
        <HiringDemandTab period={period} />
      ) : (
        <CapacityTab period={period} />
      )}
    </div>
  );
}
